import type { PlanKey } from "@/lib/pricing";

const KEY = "pendingCheckoutPlan";
const CERT_KEY = "pendingCheckoutCert";
const VALID: PlanKey[] = ["monthly", "quarterly", "yearly"];

// Use localStorage so the value survives:
//  - Email verification opening in a new tab
//  - Full-page OAuth redirects that clear sessionStorage
function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try { return window.localStorage; } catch { return null; }
}

export function savePendingPlan(plan: string | null | undefined): void {
  if (!plan) return;
  const s = storage();
  if (!s) return;
  if (!VALID.includes(plan as PlanKey)) return;
  try { s.setItem(KEY, plan); } catch { /* noop */ }
}

export function consumePendingPlan(): PlanKey | null {
  const s = storage();
  if (!s) return null;
  try {
    const v = s.getItem(KEY);
    if (v) s.removeItem(KEY);
    return VALID.includes(v as PlanKey) ? (v as PlanKey) : null;
  } catch { return null; }
}

export function clearPendingCheckoutIntent(): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(KEY);
    s.removeItem(CERT_KEY);
  } catch { /* noop */ }
}

export function peekPendingPlan(): PlanKey | null {
  const s = storage();
  if (!s) return null;
  try {
    const v = s.getItem(KEY);
    return VALID.includes(v as PlanKey) ? (v as PlanKey) : null;
  } catch { return null; }
}

// Optional certification id to grant on successful checkout.
// Persisted alongside the plan so a logged-out user can pick a certification
// + plan on Landing/Pricing, complete signup or login (potentially through
// email verification / OAuth round-trips that clear sessionStorage), and
// land back on Pricing with both pieces of context intact.
function isValidCertId(v: unknown): v is string {
  return typeof v === "string" && /^[a-zA-Z0-9_.-]{1,128}$/.test(v);
}

export function savePendingCert(certId: string | null | undefined): void {
  const s = storage();
  if (!s) return;
  try {
    if (!certId) { s.removeItem(CERT_KEY); return; }
    if (!isValidCertId(certId)) return;
    s.setItem(CERT_KEY, certId);
  } catch { /* noop */ }
}

export function peekPendingCert(): string | null {
  const s = storage();
  if (!s) return null;
  try {
    const v = s.getItem(CERT_KEY);
    return isValidCertId(v) ? v : null;
  } catch { return null; }
}

export function consumePendingCert(): string | null {
  const s = storage();
  if (!s) return null;
  try {
    const v = s.getItem(CERT_KEY);
    if (v) s.removeItem(CERT_KEY);
    return isValidCertId(v) ? v : null;
  } catch { return null; }
}